import React, { useState, useEffect, useCallback } from 'react';
import './Testimonials.css';

const Testimonials = ({ title, subtitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: 'Mrs. Adaeze Okafor',
      role: 'Parent',
      image: '/assets/images/testimonials/parent1.jpg',
      text: 'The MINT School has been a blessing to our family. My daughter has grown not just academically, but also in character and confidence. The teachers are truly dedicated to each child\'s success.',
      rating: 5
    },
    {
      id: 2,
      name: 'Chinedu Okoli',
      role: 'Alumni, Class of 2022',
      image: '/assets/images/testimonials/student1.jpg',
      text: 'MINT School prepared me exceptionally well for university. The solid foundation I received here has made my transition to higher education smooth. I\'m forever grateful to my teachers and mentors.',
      rating: 5
    },
    {
      id: 3,
      name: 'Dr. Emmanuel Agu',
      role: 'Parent',
      image: '/assets/images/testimonials/parent2.jpg',
      text: 'As a medical professional, I appreciate the school\'s emphasis on science and critical thinking. My son\'s passion for medicine was nurtured here. The facilities and curriculum are outstanding.',
      rating: 5
    },
    {
      id: 4,
      name: 'Blessing Idoko',
      role: 'Current Student, SSS 2',
      image: '/assets/images/testimonials/student2.jpg',
      text: 'I love MINT School! The teachers make learning fun and interesting. I\'ve made great friends and discovered my love for mathematics through the Math Club. This school is the best!',
      rating: 5
    },
    {
      id: 5,
      name: 'Mrs. Grace Onyeka',
      role: 'Parent',
      image: '/assets/images/testimonials/parent3.jpg',
      text: 'The holistic approach to education at MINT School is commendable. My children are not only excelling academically but also developing important life skills through sports and extracurricular activities.',
      rating: 5
    }
  ];

  const nextTestimonial = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [isAnimating, testimonials.length]);

  const prevTestimonial = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const goToTestimonial = (index) => {
    if (!isAnimating && index !== currentIndex) {
      setIsAnimating(true);
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  useEffect(() => {
    const autoPlay = setInterval(() => {
      nextTestimonial();
    }, 7000);

    return () => clearInterval(autoPlay);
  }, [currentIndex, nextTestimonial]);

  const current = testimonials[currentIndex];

  return (
    <section className="testimonials-section">
      <div className="testimonials-background">
        <div className="testimonials-pattern"></div>
      </div>

      <div className="container">
        {title && <h2 className="section-title testimonials-title">{title}</h2>}
        {subtitle && <p className="section-subtitle testimonials-subtitle">{subtitle}</p>}

        <div className="testimonials-carousel">
          <div className={`testimonial-card ${isAnimating ? 'animating' : ''}`}>
            <div className="testimonial-header">
              <div className="testimonial-image">
                <img src={current.image} alt={current.name} />
                <div className="quote-badge">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 9V5C10 3.34315 8.65685 2 7 2C5.34315 2 4 3.34315 4 5V9C4 13.4183 7.58172 17 12 17" stroke="currentColor" strokeWidth="2"/>
                    <path d="M20 9V5C20 3.34315 18.6569 2 17 2C15.3431 2 14 3.34315 14 5V9C14 13.4183 17.5817 17 22 17" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>

              <div className="testimonial-info">
                <h3>{current.name}</h3>
                <p className="testimonial-role">{current.role}</p>
                <div className="testimonial-rating">
                  {[...Array(current.rating)].map((_, i) => (
                    <svg key={i} className="star" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                  ))}
                </div>
              </div>
            </div>

            <div className="testimonial-content">
              <p>"{current.text}"</p>
            </div>
          </div>

          {/* Navigation */}
          <button className="testimonial-nav testimonial-nav--prev" onClick={prevTestimonial}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <button className="testimonial-nav testimonial-nav--next" onClick={nextTestimonial}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Dots Navigation */}
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToTestimonial(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Thumbnail Preview */}
        <div className="testimonials-thumbnails">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToTestimonial(index)}
            >
              <img src={testimonial.image} alt={testimonial.name} />
              <div className="thumbnail-overlay">
                <span>{testimonial.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
